import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CustomElement, customElementsApi } from '@/api/customElements';
import type {
  FileEntryCondition,
  FormSection,
  WorkflowFieldPropertiesPayload,
  WorkflowStep,
} from '@/api/workflows';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import {
  asDragData,
  DRAG_ACTIVATION_DISTANCE_PX,
  resolveDrop,
  type BuilderDragData,
} from './builderDnd';
import { stripSectionForCustomElement } from './customElements/conditionStripping';
import { SaveAsCustomElementModal } from './customElements/SaveAsCustomElementModal';
import { sectionToPayload } from './customElements/toCustomPayload';
import { type PaletteEntry } from './fieldMeta';
import { FieldPalette } from './FieldPalette';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';
import { FormCanvas } from './FormCanvas';
import type { FormEditorDataSource } from './FormEditorDataSource';
import { SectionPropertiesPanel } from './SectionPropertiesPanel';

type Location = { sectionId: string; rowId: string; columnId: string };

type Props = {
  sections: FormSection[];
  dataSource: FormEditorDataSource;
  /**
   * Owning workflow + step. Threaded down to the visibility-conditions UI and the
   * field-help-image upload, both of which hide/no-op when undefined — the Custom
   * Editor passes neither since a detached library item has no workflow context for
   * either feature. Real workflow editing always passes both.
   */
  templateId: string | undefined;
  stepId: string | undefined;
  /** Every DynamicForm step in the owning workflow, for the conditions source-field picker. Empty when there's no owning workflow. */
  allSteps: WorkflowStep[];
  onPreview?: () => void;
  /** True while another session holds the lock (or any other reason to freeze the panel). Purely visual — the caller still owns the gate that actually disables individual API calls upstream. */
  disabled?: boolean;
  /** Custom Editor's Section mode: exactly one section, so "+ Add Section" is hidden (a Section custom element has no concept of a second section). */
  singleSectionMode?: boolean;
  /** Custom Editor's Step mode: renders a "Step details" button beside Preview. */
  onEditStepDetails?: () => void;
  /**
   * When true, an empty canvas seeds itself once with a section + row on mount
   * (see the effect below). Real workflow DynamicForm steps opt in so a freshly
   * created step opens ready to author instead of on the bare empty state; the
   * Custom Editor leaves it off (a saved library item shouldn't self-populate).
   */
  autoSeedWhenEmpty?: boolean;
  /**
   * Caps the 3-panel row height (e.g. `'80vh'`). When set, the palette and
   * canvas each scroll within their own column instead of growing the page.
   */
  maxHeight?: number | string;
  /** Client-side conditions handler — passed through to FieldPropertiesPanel for
   *  custom element editors that have no real templateId. */
  onSaveFieldConditions?: (
    sectionId: string,
    rowId: string,
    columnId: string,
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => Promise<void>;
  /** Client-side section conditions handler — passed through to FormCanvas for
   *  the custom step editor which has no real templateId. */
  onSaveSectionConditions?: (
    sectionId: string,
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => Promise<void>;
};

/**
 * The 3-panel form-builder UI (palette → canvas → properties), extracted out of
 * `DynamicFormBuilderPage` so it can be reused by the Custom Editor (editing a
 * saved Section/Step library item, with no real workflow underneath). All canvas
 * mutations route through `dataSource` instead of calling `workflowsApi` directly;
 * `DynamicFormBuilderPage` builds a workflow-backed data source via
 * `createWorkflowDataSource`, while the Custom Editor backs the same shell with an
 * in-memory one that re-saves the whole section/step on every change.
 */
export function DynamicFormEditorShell({
  sections,
  dataSource,
  templateId,
  stepId,
  allSteps,
  onPreview,
  disabled = false,
  singleSectionMode = false,
  onEditStepDetails,
  onSaveFieldConditions,
  onSaveSectionConditions,
  maxHeight,
  autoSeedWhenEmpty = false,
}: Props) {
  const { t } = useTranslation();

  const [pendingEntry, setPendingEntry] = useState<PaletteEntry | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  // Section selected for the Section Properties panel. Mutually exclusive with
  // a field selection — the right rail shows one panel or the other. Selecting
  // a section clears the field selection (and vice versa) so the two never
  // fight over the rail.
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  // "Armed for move" — when set, the canvas shows a dashed-outline source
  // marker on the picked-up field and highlights every empty column across
  // the step as a drop target. Click an empty column → moveField; click the
  // source again or press Esc → cancel. Mutually exclusive with palette
  // arming (each arm-action clears the other so the canvas never has two
  // pending-place affordances active at once).
  const [movingFrom, setMovingFrom] = useState<Location | null>(null);
  // Section currently being named in the save-as-custom modal; null hides it.
  const [saveSectionTarget, setSaveSectionTarget] = useState<FormSection | null>(null);
  const [savingSectionAsCustom, setSavingSectionAsCustom] = useState(false);

  const selectedField = useMemo(() => {
    if (!selectedLocation) return null;
    const section = sections.find((s) => s.id === selectedLocation.sectionId);
    const row = section?.rows.find((r) => r.id === selectedLocation.rowId);
    const col = row?.columns.find((c) => c.id === selectedLocation.columnId);
    return col?.field ?? null;
  }, [sections, selectedLocation]);

  const selectedSection = useMemo(
    () => (selectedSectionId ? (sections.find((s) => s.id === selectedSectionId) ?? null) : null),
    [sections, selectedSectionId],
  );

  // Section Properties is a workflow-backed-only feature (the repeatable
  // endpoint needs a real template). Gate the whole affordance on the data
  // source exposing both handlers so the Custom Editor never shows it.
  const sectionPropsEnabled =
    dataSource.editSectionDetails !== undefined && dataSource.setSectionRepeatable !== undefined;

  const handleSelectSection = useCallback((sectionId: string) => {
    setSelectedSectionId(sectionId);
    setSelectedLocation(null);
    setPendingEntry(null);
    setMovingFrom(null);
  }, []);

  const handleSaveSectionDetails = useCallback(
    async (sectionId: string, payload: { titleEn: string; titleFr: string | null }) => {
      if (!dataSource.editSectionDetails) return;
      await dataSource.editSectionDetails(sectionId, payload);
      await dataSource.refresh();
    },
    [dataSource],
  );

  const handleSaveSectionRepeatable = useCallback(
    async (
      sectionId: string,
      payload: { repeatable: boolean; minInstances: number; maxInstances: number | null },
    ) => {
      if (!dataSource.setSectionRepeatable) return;
      await dataSource.setSectionRepeatable(sectionId, payload);
      await dataSource.refresh();
    },
    [dataSource],
  );

  // ---- Section operations ----

  const handleAddSection = useCallback(async () => {
    try {
      // A brand-new section always starts with one row so the originator has an
      // immediate drop target — an empty section is never a useful end state.
      const section = await dataSource.addSection({
        titleEn: t('workflow.builder.section.defaultTitle', { n: sections.length + 1 }),
      });
      await dataSource.addRow(section.id);
      await dataSource.refresh();
    } catch (err) {
      handleApiError(err, t, t('workflow.builder.errors.sectionAddFailed'));
    }
  }, [dataSource, sections.length, t]);

  // First-visit seed: an empty DynamicForm step opens with a section + row
  // already in place (handleAddSection creates both) so the originator lands on
  // a ready canvas rather than the bare empty state. The ref guard fires this at
  // most once per mount — if the step already has content, or once we've seeded,
  // it never runs again (so deleting every section by hand doesn't re-seed).
  // Skipped while disabled (locked by another editor) so we don't mutate a step
  // we can't edit; it'll seed on a later mount once the lock clears.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!autoSeedWhenEmpty || disabled || seededRef.current) return;
    if (sections.length > 0) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    void handleAddSection();
  }, [autoSeedWhenEmpty, disabled, sections.length, handleAddSection]);

  const handleEditSectionTitle = useCallback(
    async (sectionId: string, titleEn: string) => {
      try {
        await dataSource.editSectionTitle(sectionId, titleEn);
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.sectionEditFailed'));
      }
    },
    [dataSource, t],
  );

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      try {
        await dataSource.deleteSection(sectionId);
        setSelectedLocation((cur) => (cur?.sectionId === sectionId ? null : cur));
        setSelectedSectionId((cur) => (cur === sectionId ? null : cur));
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.sectionDeleteFailed'));
      }
    },
    [dataSource, t],
  );

  const handleMoveSection = useCallback(
    async (sectionId: string, direction: 'Up' | 'Down') => {
      try {
        await dataSource.moveSection(sectionId, direction);
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.sectionMoveFailed'));
      }
    },
    [dataSource, t],
  );

  // ---- Row operations ----

  const handleAddRow = useCallback(
    async (sectionId: string) => {
      try {
        await dataSource.addRow(sectionId);
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.rowAddFailed'));
      }
    },
    [dataSource, t],
  );

  const handleDeleteRow = useCallback(
    async (sectionId: string, rowId: string) => {
      try {
        await dataSource.deleteRow(sectionId, rowId);
        setSelectedLocation((cur) => (cur?.rowId === rowId ? null : cur));
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.rowDeleteFailed'));
      }
    },
    [dataSource, t],
  );

  const handleMoveRow = useCallback(
    async (sectionId: string, rowId: string, direction: 'Up' | 'Down') => {
      try {
        await dataSource.moveRow(sectionId, rowId, direction);
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.rowMoveFailed'));
      }
    },
    [dataSource, t],
  );

  // ---- Column operations ----

  const handleAddColumn = useCallback(
    async (sectionId: string, rowId: string) => {
      try {
        await dataSource.addColumn(sectionId, rowId);
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.columnAddFailed'));
      }
    },
    [dataSource, t],
  );

  const handleRemoveColumn = useCallback(
    async (sectionId: string, rowId: string, columnId: string) => {
      try {
        await dataSource.removeColumn(sectionId, rowId, columnId);
        setSelectedLocation((cur) => (cur?.columnId === columnId ? null : cur));
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.columnRemoveFailed'));
      }
    },
    [dataSource, t],
  );

  // ---- Field operations ----

  const handlePlaceField = useCallback(
    // `entry` lets a DROP name what it is carrying. The click path leaves it
    // undefined and falls back to the armed palette entry; drag can't do that
    // because arming is a state update that hasn't committed by the time
    // onDragEnd runs.
    async (sectionId: string, rowId: string, columnId: string, entry?: PaletteEntry) => {
      const source = entry ?? pendingEntry;
      if (!source || (source.kind !== 'field' && source.kind !== 'customField')) {
        return;
      }
      try {
        await dataSource.placeField(sectionId, rowId, columnId, {
          type: source.type,
          labelEn: source.defaultLabelEn,
          // A custom field carries over whatever FR label the originator
          // authored when saving it. Built-in catalog cards have no FR
          // default, so seed a visible "fr-" placeholder from the EN default
          // (e.g. "fr-Text field") — the required-FR rule in the properties
          // panel would otherwise block the first save, and the prefix makes
          // untranslated labels easy to spot.
          labelFr:
            (source.kind === 'customField' ? source.defaultLabelFr : null) ??
            `fr-${source.defaultLabelEn}`,
          // Built-in catalog cards always place as optional; a custom field
          // carries over the `required` flag the originator saved (readOnly and
          // the rest ride along inside defaultProperties()).
          required: source.kind === 'customField' ? source.defaultRequired : false,
          properties: source.defaultProperties(),
        });
        setPendingEntry(null);
        setSelectedSectionId(null);
        setSelectedLocation({ sectionId, rowId, columnId });
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.fieldPlaceFailed'));
      }
    },
    [dataSource, pendingEntry, t],
  );

  const handleSelectField = useCallback((sectionId: string, rowId: string, columnId: string) => {
    setSelectedLocation({ sectionId, rowId, columnId });
    setSelectedSectionId(null);
    setPendingEntry(null);
    setMovingFrom(null);
  }, []);

  const handleArmMove = useCallback((sectionId: string, rowId: string, columnId: string) => {
    // Mutually exclusive with palette arming + properties-panel selection.
    setPendingEntry(null);
    setSelectedLocation(null);
    setSelectedSectionId(null);
    setMovingFrom((cur) =>
      cur && cur.columnId === columnId ? null : { sectionId, rowId, columnId },
    );
  }, []);

  const handlePlaceMove = useCallback(
    // `from` mirrors handlePlaceField's `entry` — a drop knows its own source,
    // a click relies on the armed one.
    async (toSectionId: string, toRowId: string, toColumnId: string, from?: Location) => {
      const origin = from ?? movingFrom;
      if (!origin) return;
      // No-op for a self-drop (clicking or dropping on the source column).
      if (
        origin.sectionId === toSectionId &&
        origin.rowId === toRowId &&
        origin.columnId === toColumnId
      ) {
        setMovingFrom(null);
        return;
      }
      try {
        await dataSource.moveField({
          fromSectionId: origin.sectionId,
          fromRowId: origin.rowId,
          fromColumnId: origin.columnId,
          toSectionId,
          toRowId,
          toColumnId,
        });
        setMovingFrom(null);
        // Selection follows the field so the properties panel stays open
        // on the moved field, not on the old (now empty) source.
        setSelectedLocation({ sectionId: toSectionId, rowId: toRowId, columnId: toColumnId });
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.builder.errors.fieldMoveFailed'));
      }
    },
    [dataSource, movingFrom, t],
  );

  const handleCancelMove = useCallback(() => {
    setMovingFrom(null);
  }, []);

  // ---- Drag and drop (enhancement over arm-and-place; see builderDnd.ts) ----

  // What is currently in the air. Drives the drag preview and, critically,
  // suppresses hover tooltips on the canvas — tooltips covering drop targets
  // mid-drag is the exact bug that got drag removed from this canvas in June
  // (f8eb21b), so it is designed out rather than patched later.
  const [dragging, setDragging] = useState<BuilderDragData | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Must be non-zero: these same elements are click targets for
      // arm-and-place, and a 0px activation distance eats the click.
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = asDragData(event.active.data.current);
    setDragging(data);
    // A drag supersedes any armed state, so the two mechanisms can't both
    // think they own the next click.
    if (data?.kind === 'palette') {
      setMovingFrom(null);
    } else if (data?.kind === 'placedField') {
      setPendingEntry(null);
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDragging(null);
      // Rules live in resolveDrop so the rejections (occupied slot, self-drop,
      // foreign payload) are unit-testable without a DOM.
      const outcome = resolveDrop(event.active.data.current, event.over?.data.current);
      if (outcome.action === 'place') {
        void handlePlaceField(
          outcome.to.sectionId,
          outcome.to.rowId,
          outcome.to.columnId,
          outcome.entry,
        );
      } else if (outcome.action === 'move') {
        void handlePlaceMove(
          outcome.to.sectionId,
          outcome.to.rowId,
          outcome.to.columnId,
          outcome.from,
        );
      }
    },
    [handlePlaceField, handlePlaceMove],
  );

  const handleDragCancel = useCallback(() => setDragging(null), []);

  // Race-free re-entry guard for compound insertion. A useRef is set
  // synchronously before the first await, so a rapid double-click sees the
  // truth immediately (vs reading React state which is stale until the
  // re-render).
  const compoundInsertingRef = useRef(false);

  /**
   * Palette card click handler. Field cards arm (clicking an empty column on
   * the canvas places the field); compound cards skip arming and immediately
   * create a new section pre-populated with the blueprint's rows. The
   * compound flow is one section-create + one insert-compound batch + one
   * refresh — with a re-entry guard so double-clicks don't duplicate the
   * section, and a best-effort rollback so a failed compound-insert deletes
   * the section we just created (leaving an orphan would force the
   * originator to delete the empty section manually).
   */
  const handlePalettePick = useCallback(
    async (entry: PaletteEntry | null) => {
      // Toggle the existing pending entry off (re-clicking the armed card).
      if (entry === null) {
        setPendingEntry(null);
        return;
      }
      if (entry.kind === 'field' || entry.kind === 'customField') {
        // Field arming stays the same — toggle on / off / swap. Also clear
        // any in-flight move arm so the two flows don't overlap on the
        // canvas.
        setMovingFrom(null);
        setPendingEntry((cur) => (cur?.id === entry.id ? null : entry));
        return;
      }
      // Compound: skip arming. Create a new section with the compound's
      // default title, then drop the blueprint rows + fields in via the
      // batch endpoint. Re-fetch once at the end.
      if (compoundInsertingRef.current) return;
      compoundInsertingRef.current = true;
      // Clear any field-armed state so the canvas doesn't show place-here
      // hints during the round-trip.
      setPendingEntry(null);
      let createdSectionId: string | null = null;
      try {
        const newSection = await dataSource.addSection({
          titleEn: entry.defaultSectionTitleEn,
          titleFr: entry.defaultSectionTitleFr,
        });
        createdSectionId = newSection.id;
        await dataSource.insertCompound(newSection.id, {
          rows: entry.blueprint.rows.map((row) => ({
            columns: row.columns.map((spec) =>
              spec === null
                ? null
                : {
                    type: spec.type,
                    labelEn: spec.labelEn,
                    labelFr: spec.labelFr ?? null,
                    required: spec.required,
                    properties: spec.properties,
                  },
            ),
          })),
        });
        await dataSource.refresh();
      } catch (err) {
        // Best-effort rollback: if the section was created but the compound
        // insert failed, delete the orphan so the originator doesn't have to
        // clean it up. Swallow rollback errors — surface the original cause.
        if (createdSectionId !== null) {
          try {
            await dataSource.deleteSection(createdSectionId);
            await dataSource.refresh();
          } catch {
            // Rollback itself failed; the orphan will show up on the next
            // refresh and the originator can remove it manually.
          }
        }
        handleApiError(err, t, t('workflow.builder.errors.compoundInsertFailed'));
      } finally {
        compoundInsertingRef.current = false;
      }
    },
    [dataSource, t],
  );

  const handleSaveProperties = useCallback(
    async (payload: {
      labelEn: string;
      labelFr?: string | null;
      required: boolean;
      properties: WorkflowFieldPropertiesPayload;
    }) => {
      if (!selectedLocation) return;
      // Auto-save fires this every quiet-window (~500 ms); a success toast
      // on every PATCH would spam the top of the page. The properties
      // panel's coloured-dot indicator already surfaces save state more
      // ambiently — let it own the success signal. Errors still bubble
      // (handleApiError raises a sticky message) AND the panel's dot
      // flips red, so failure can't slip through silently.
      await dataSource.editField(
        selectedLocation.sectionId,
        selectedLocation.rowId,
        selectedLocation.columnId,
        payload,
      );
      await dataSource.refresh();
    },
    [dataSource, selectedLocation],
  );

  const handleRemoveField = useCallback(async () => {
    if (!selectedLocation) return;
    try {
      await dataSource.removeField(
        selectedLocation.sectionId,
        selectedLocation.rowId,
        selectedLocation.columnId,
      );
      setSelectedLocation(null);
      await dataSource.refresh();
    } catch (err) {
      handleApiError(err, t, t('workflow.builder.errors.fieldRemoveFailed'));
    }
  }, [dataSource, selectedLocation, t]);

  const handlePickCustomSection = useCallback(
    async (el: CustomElement) => {
      if (!dataSource.placeCustomSection) return;
      try {
        await dataSource.placeCustomSection(el.id);
        await dataSource.refresh();
      } catch (err) {
        handleApiError(err, t, t('workflow.customElements.insertSectionFailed'));
      }
    },
    [dataSource, t],
  );

  const handleSaveSectionAsCustom = useCallback(
    async ({ nameEn, nameFr }: { nameEn: string; nameFr: string | null }) => {
      if (!saveSectionTarget) return;
      setSavingSectionAsCustom(true);
      try {
        const stripped = stripSectionForCustomElement(saveSectionTarget);
        await customElementsApi.saveSection(nameEn, nameFr, sectionToPayload(stripped));
        void message.success(t('workflow.customElements.savedSection'));
        setSaveSectionTarget(null);
      } catch (err) {
        handleApiError(err, t, t('workflow.customElements.saveFailed'));
      } finally {
        setSavingSectionAsCustom(false);
      }
    },
    [saveSectionTarget, t],
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div
          style={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            maxHeight,
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            background: '#fff',
            pointerEvents: disabled ? 'none' : 'auto',
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <FieldPalette
            selectedEntry={pendingEntry}
            onSelect={handlePalettePick}
            onPickCustomSection={handlePickCustomSection}
            hideSections={singleSectionMode}
          />

          <FormCanvas
            sections={sections}
            isPalettePending={pendingEntry !== null}
            selectedColumnId={selectedLocation?.columnId ?? null}
            movingFrom={movingFrom}
            onAddSection={handleAddSection}
            onEditSectionTitle={handleEditSectionTitle}
            onDeleteSection={handleDeleteSection}
            onMoveSection={handleMoveSection}
            onAddRow={handleAddRow}
            onDeleteRow={handleDeleteRow}
            onMoveRow={handleMoveRow}
            onAddColumn={handleAddColumn}
            onRemoveColumn={handleRemoveColumn}
            onPlaceField={handlePlaceField}
            onSelectField={handleSelectField}
            onArmMove={handleArmMove}
            onPlaceMove={handlePlaceMove}
            onCancelMove={handleCancelMove}
            dragging={dragging}
            onPreview={onPreview}
            templateId={templateId}
            stepId={stepId}
            allSteps={allSteps}
            onConditionsChanged={dataSource.refresh}
            onSaveSectionAsCustom={setSaveSectionTarget}
            hideAddSection={singleSectionMode}
            onEditStepDetails={onEditStepDetails}
            onSaveSectionConditions={onSaveSectionConditions}
            onSelectSection={sectionPropsEnabled ? handleSelectSection : undefined}
            selectedSectionId={selectedSectionId}
          />

          {/* Right rail shows Section Properties when a section is selected,
            otherwise the Field Properties panel. Selection is mutually
            exclusive so at most one is ever non-null. */}
          {selectedSection ? (
            <SectionPropertiesPanel
              section={selectedSection}
              onSaveDetails={(payload) => handleSaveSectionDetails(selectedSection.id, payload)}
              onSaveRepeatable={(payload) =>
                handleSaveSectionRepeatable(selectedSection.id, payload)
              }
              onClose={() => setSelectedSectionId(null)}
            />
          ) : (
            <FieldPropertiesPanel
              field={selectedField}
              templateId={templateId}
              stepId={stepId}
              location={selectedLocation}
              // Visibility-condition modal walks every DynamicForm step's fields
              // to populate the source-field picker — pass the whole step list so
              // a field in step 2 can gate on an answer in step 1.
              allSteps={allSteps}
              onSave={handleSaveProperties}
              onRemove={handleRemoveField}
              onConditionsChanged={dataSource.refresh}
              onClose={() => setSelectedLocation(null)}
              onSaveFieldConditions={onSaveFieldConditions}
            />
          )}
        </div>
        {/* Follows the cursor so the originator can see WHAT they are dragging
            over a canvas of near-identical empty slots. Rendered in a portal by
            dnd-kit, so it is not clipped by the panel's overflow:hidden. */}
        <DragOverlay dropAnimation={null}>
          {dragging ? (
            <div
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                background: '#0179C1',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {dragging.kind === 'palette'
                ? dragging.entry.defaultLabelEn
                : t('workflow.builder.dnd.movingField')}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <SaveAsCustomElementModal
        open={saveSectionTarget !== null}
        kind="Section"
        saving={savingSectionAsCustom}
        onCancel={() => setSaveSectionTarget(null)}
        onSubmit={handleSaveSectionAsCustom}
      />
    </>
  );
}
